import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { bulkDeleteLeads, deleteAllLeads } from '../../lib/api';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { LeadCard } from './LeadCard';
import { Search, Filter, Trash2, CheckSquare, Square, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export const LeadList = ({ 
  leads, 
  selectedLead, 
  onSelectLead, 
  searchQuery, 
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  onLeadsDeleted
}) => {
  const { user } = useAuth();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const handleSelectLead = (leadId, checked) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(leadId);
    } else {
      newSelected.delete(leadId);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedIds.size === leads.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(leads.map(l => l.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    
    if (!window.confirm(`Delete ${selectedIds.size} selected lead(s)? This cannot be undone.`)) return;
    
    setDeleting(true);
    try {
      await bulkDeleteLeads(Array.from(selectedIds));
      toast.success(`Deleted ${selectedIds.size} leads`);
      setSelectedIds(new Set());
      if (onLeadsDeleted) onLeadsDeleted();
    } catch (err) {
      toast.error('Failed to delete leads');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAll = async () => {
    if (leads.length === 0) {
      toast.error('No leads to delete');
      return;
    }
    
    if (!window.confirm(`DELETE ALL ${leads.length} LEADS? This action CANNOT be undone!`)) return;
    if (!window.confirm(`Are you ABSOLUTELY sure? Type "yes" in your mind and click OK.`)) return;
    
    setDeleting(true);
    try {
      await deleteAllLeads();
      toast.success('All leads deleted');
      setSelectedIds(new Set());
      if (onLeadsDeleted) onLeadsDeleted();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete all leads');
    } finally {
      setDeleting(false);
    }
  };

  const allSelected = leads.length > 0 && selectedIds.size === leads.length;
  const someSelected = selectedIds.size > 0;

  return (
    <div className="h-full flex flex-col bg-background" data-testid="lead-list">
      {/* Search and Filter */}
      <div className="p-4 border-b space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
            data-testid="search-input"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={onStatusFilterChange}>
            <SelectTrigger className="h-8 text-xs flex-1" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="qualified">Qualified</SelectItem>
              <SelectItem value="proposal">Proposal</SelectItem>
              <SelectItem value="won">Won</SelectItem>
              <SelectItem value="lost">Lost</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {leads.length} lead{leads.length !== 1 ? 's' : ''} found
          </p>
          
          {/* Bulk Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={handleSelectAll}
              data-testid="select-all-btn"
            >
              {allSelected ? (
                <><CheckSquare className="w-3 h-3 mr-1" /> Deselect</>
              ) : (
                <><Square className="w-3 h-3 mr-1" /> Select All</>
              )}
            </Button>
            
            {user?.role === 'admin' && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                onClick={handleDeleteAll}
                disabled={deleting || leads.length === 0}
                data-testid="delete-all-btn"
              >
                <AlertTriangle className="w-3 h-3 mr-1" /> Delete All
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Action Bar - Shows when items selected */}
      {someSelected && (
        <div className="px-4 py-2 bg-accent/10 border-b flex items-center justify-between">
          <span className="text-sm font-medium">
            {selectedIds.size} lead{selectedIds.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDelete}
              disabled={deleting}
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {deleting ? 'Deleting...' : 'Delete Selected'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Lead Cards */}
      <ScrollArea className="flex-1">
        {leads.length === 0 ? (
          <div className="p-8 text-center">
            <div className="empty-state-bg rounded-lg" />
            <p className="text-muted-foreground">No leads found</p>
            <p className="text-sm text-muted-foreground/70 mt-1">Import a CSV or add leads manually</p>
          </div>
        ) : (
          <div className="divide-y">
            {leads.map((lead) => (
              <div key={lead.id} className="flex items-center">
                <div className="pl-4 pr-2">
                  <Checkbox
                    checked={selectedIds.has(lead.id)}
                    onCheckedChange={(checked) => handleSelectLead(lead.id, checked)}
                    data-testid={`select-lead-${lead.id}`}
                  />
                </div>
                <div className="flex-1">
                  <LeadCard
                    lead={lead}
                    isSelected={selectedLead?.id === lead.id}
                    onClick={() => onSelectLead(lead)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};
